import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Country {
  iso2: string;
  dialCode: string;
}

// Curated list focused on Latin America (primary market) plus the most common
// international origins. ISO 3166-1 alpha-2 + E.164 country code.
const COUNTRIES: Country[] = [
  { iso2: 'MX', dialCode: '+52' },
  { iso2: 'US', dialCode: '+1' },
  { iso2: 'CA', dialCode: '+1' },
  { iso2: 'AR', dialCode: '+54' },
  { iso2: 'BR', dialCode: '+55' },
  { iso2: 'CL', dialCode: '+56' },
  { iso2: 'CO', dialCode: '+57' },
  { iso2: 'PE', dialCode: '+51' },
  { iso2: 'EC', dialCode: '+593' },
  { iso2: 'VE', dialCode: '+58' },
  { iso2: 'BO', dialCode: '+591' },
  { iso2: 'PY', dialCode: '+595' },
  { iso2: 'UY', dialCode: '+598' },
  { iso2: 'CR', dialCode: '+506' },
  { iso2: 'PA', dialCode: '+507' },
  { iso2: 'GT', dialCode: '+502' },
  { iso2: 'SV', dialCode: '+503' },
  { iso2: 'HN', dialCode: '+504' },
  { iso2: 'NI', dialCode: '+505' },
  { iso2: 'CU', dialCode: '+53' },
  { iso2: 'DO', dialCode: '+1' },
  { iso2: 'PR', dialCode: '+1' },
  { iso2: 'ES', dialCode: '+34' },
  { iso2: 'GB', dialCode: '+44' },
  { iso2: 'FR', dialCode: '+33' },
  { iso2: 'DE', dialCode: '+49' },
  { iso2: 'IT', dialCode: '+39' },
  { iso2: 'PT', dialCode: '+351' },
  { iso2: 'NL', dialCode: '+31' },
  { iso2: 'BE', dialCode: '+32' },
  { iso2: 'CH', dialCode: '+41' },
  { iso2: 'AT', dialCode: '+43' },
  { iso2: 'IE', dialCode: '+353' },
  { iso2: 'AU', dialCode: '+61' },
  { iso2: 'NZ', dialCode: '+64' },
  { iso2: 'JP', dialCode: '+81' },
  { iso2: 'KR', dialCode: '+82' },
  { iso2: 'CN', dialCode: '+86' },
  { iso2: 'IN', dialCode: '+91' },
];

const DEFAULT_BY_LOCALE: Record<string, string> = {
  es: 'MX',
  en: 'US',
};

function flagEmoji(iso2: string): string {
  const base = 0x1f1e6 - 'A'.charCodeAt(0);
  return [...iso2.toUpperCase()].map((c) => String.fromCodePoint(base + c.charCodeAt(0))).join('');
}

interface PhoneInputProps {
  value: string;
  onChange: (full: string) => void;
  id?: string;
  required?: boolean;
}

export function PhoneInput({ value, onChange, id, required }: PhoneInputProps) {
  const { t, i18n } = useTranslation('common');
  const locale = i18n.language?.split('-')[0] ?? 'es';

  const [country, setCountry] = useState<string>(() => DEFAULT_BY_LOCALE[locale] ?? 'MX');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => COUNTRIES.find((c) => c.iso2 === country) ?? COUNTRIES[0]!,
    [country],
  );

  const regionNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([locale], { type: 'region' });
    } catch {
      return new Intl.DisplayNames(['en'], { type: 'region' });
    }
  }, [locale]);

  // Strip the current dial code from the controlled value to derive the local
  // digits. If the value somehow doesn't match (e.g. user switched country
  // mid-typing) fall back to whatever digits remain.
  const localDigits = useMemo(() => {
    if (!value) return '';
    if (value.startsWith(selected.dialCode)) {
      return value.slice(selected.dialCode.length);
    }
    return value.replace(/\D/g, '');
  }, [value, selected]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    // Focus the search box on open without scrolling the page.
    queueMicrotask(() => searchRef.current?.focus({ preventScroll: true }));
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) => {
      const name = (regionNames.of(c.iso2) ?? '').toLowerCase();
      return name.includes(q) || c.iso2.toLowerCase().includes(q) || c.dialCode.includes(q);
    });
  }, [search, regionNames]);

  function handleLocalChange(raw: string) {
    const digits = raw.replace(/\D/g, '');
    if (!digits) {
      onChange('');
      return;
    }
    onChange(`${selected.dialCode}${digits}`);
  }

  function handleSelect(iso2: string) {
    const next = COUNTRIES.find((c) => c.iso2 === iso2);
    if (!next) return;
    setCountry(iso2);
    setOpen(false);
    setSearch('');
    if (localDigits) onChange(`${next.dialCode}${localDigits}`);
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex items-stretch gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={t('phone_input.select_country')}
          className="flex items-center gap-2 bg-neutral-900 border border-neutral-800 rounded-lg px-3 py-3 hover:border-neutral-600 focus:border-neutral-600 transition shrink-0 outline-none"
        >
          <span className="text-lg leading-none" aria-hidden>
            {flagEmoji(selected.iso2)}
          </span>
          <span className="text-sm text-neutral-300 tabular-nums">{selected.dialCode}</span>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`text-neutral-500 transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        <input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          required={required}
          value={localDigits}
          onChange={(e) => handleLocalChange(e.target.value)}
          className="input"
        />
      </div>
      {open && (
        <div className="absolute z-30 mt-2 w-full sm:w-80 rounded-xl border border-neutral-800 bg-neutral-950/95 backdrop-blur shadow-xl shadow-black/40 overflow-hidden">
          <div className="border-b border-neutral-800 p-2">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('phone_input.search_placeholder')}
              className="w-full bg-neutral-900 border border-neutral-800 rounded-lg px-3 py-2 text-sm outline-none focus:border-neutral-600"
            />
          </div>
          <ul role="listbox" className="max-h-64 overflow-auto p-1">
            {filtered.map((c) => {
              const active = c.iso2 === selected.iso2;
              return (
                <li key={c.iso2} role="option" aria-selected={active}>
                  <button
                    type="button"
                    onClick={() => handleSelect(c.iso2)}
                    className={[
                      'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition',
                      active
                        ? 'bg-[var(--color-accent)]/10 text-neutral-50'
                        : 'text-neutral-300 hover:bg-neutral-900',
                    ].join(' ')}
                  >
                    <span className="text-lg leading-none" aria-hidden>
                      {flagEmoji(c.iso2)}
                    </span>
                    <span className="flex-1 text-sm">{regionNames.of(c.iso2) ?? c.iso2}</span>
                    <span className="text-sm text-neutral-500 tabular-nums">{c.dialCode}</span>
                  </button>
                </li>
              );
            })}
            {filtered.length === 0 && (
              <li className="px-3 py-6 text-sm text-neutral-500 text-center">
                {t('phone_input.no_results')}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
